import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Receipt, ArrowDownLeft, ArrowUpRight, DollarSign,
  TrendingUp, Building2, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

export default function BillingPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });
  const [expandedSSP, setExpandedSSP] = useState({});
  const [expandedDSP, setExpandedDSP] = useState({});

  const fetchBilling = async (overridePeriod) => {
    setLoading(true);
    try {
      const p = overridePeriod || period;
      let url = `/billing/summary?period=${p}`;
      if (p === "custom" && customRange.start && customRange.end)
        url = `/billing/summary?period=custom&start_date=${customRange.start}&end_date=${customRange.end}`;
      const res = await api.get(url);
      setData(res.data);
      const se = {}, de = {};
      (res.data.ssp?.companies || []).forEach(c => { se[c.company_id] = true; });
      (res.data.dsp?.companies || []).forEach(c => { de[c.company_id] = true; });
      setExpandedSSP(se);
      setExpandedDSP(de);
    } catch { toast.error("Failed to load billing"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (period !== "custom") fetchBilling(period);
  }, [period]);

  const handlePeriod = (v) => {
    if (v === "custom") {
      setPeriod("custom");
    } else {
      setPeriod(v);
    }
  };
  const applyCustom = () => {
    if (!customRange.start || !customRange.end) { toast.error("Select both start and end dates"); return; }
    fetchBilling("custom");
  };

  const m = (n) => (n == null) ? "$0.000" : `$${Number(n).toFixed(3)}`;
  const f = (n) => {
    if (n == null) return "0";
    if (Math.abs(n) >= 1e6) return `${(n/1e6).toFixed(1)}M`;
    if (Math.abs(n) >= 1e3) return `${(n/1e3).toFixed(1)}K`;
    return n.toLocaleString();
  };

  if (loading || !data) return (
    <div className="flex items-center justify-center h-64" data-testid="billing-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
    </div>
  );

  const { ssp, dsp } = data;

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Receipt className="h-7 w-7 text-accent" /> Billing
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Impressions &amp; spend for SSP and DSP
            {data.date_range && <span className="ml-1 text-xs">({data.date_range.start} — {data.date_range.end})</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={period === "custom" ? "custom" : period} onValueChange={handlePeriod}>
            <SelectTrigger className="w-[170px]" data-testid="billing-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="7d">Last 7 Days</SelectItem>
              <SelectItem value="30d">Last 30 Days</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
          {period === "custom" && (
            <>
              <Input
                type="date"
                value={customRange.start}
                onChange={e => setCustomRange({...customRange, start: e.target.value, end: customRange.end && customRange.end < e.target.value ? "" : customRange.end})}
                className="w-[150px]"
                data-testid="billing-start-date"
                autoFocus
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="date"
                value={customRange.end}
                min={customRange.start || undefined}
                onChange={e => setCustomRange({...customRange, end: e.target.value})}
                className="w-[150px]"
                data-testid="billing-end-date"
              />
              <Button size="sm" onClick={applyCustom} data-testid="billing-apply-btn">Apply</Button>
            </>
          )}
        </div>
      </div>

      {/* SSP Section */}
      <Card data-testid="ssp-billing-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ArrowDownLeft className="h-5 w-5 text-blue-500" />
            SSP — Supply Side
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* SSP Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900" data-testid="ssp-impressions-card">
              <p className="text-xs text-muted-foreground uppercase">Impressions</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{f(ssp.total_impressions)}</p>
            </div>
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900" data-testid="ssp-spend-card">
              <p className="text-xs text-muted-foreground uppercase">Spend (Pay to SSP)</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{m(ssp.total_spend)}</p>
            </div>
            <div className="p-4 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900" data-testid="ssp-profit-card">
              <p className="text-xs text-muted-foreground uppercase">SSP Profit (Our Margin)</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">{m(ssp.total_profit)}</p>
            </div>
          </div>

          {/* SSP Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Company / Endpoint</TableHead>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">Impressions</TableHead>
                <TableHead className="text-right">Spend</TableHead>
                <TableHead className="text-right">Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ssp.companies.map(c => (
                <CompanyGroup
                  key={c.company_id}
                  company={c}
                  expanded={expandedSSP[c.company_id]}
                  onToggle={() => setExpandedSSP(p => ({...p, [c.company_id]: !p[c.company_id]}))}
                  moneyField="spend"
                  profitField="profit"
                  moneyColor="text-red-600"
                  profitColor="text-orange-600"
                />
              ))}
              {ssp.companies.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No SSP data</TableCell></TableRow>
              )}
              {ssp.companies.length > 0 && (
                <TableRow className="bg-muted/30 font-semibold border-t-2">
                  <TableCell /><TableCell>Total</TableCell><TableCell />
                  <TableCell className="text-right font-mono text-blue-600">{ssp.total_impressions.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-red-600">{m(ssp.total_spend)}</TableCell>
                  <TableCell className="text-right font-mono text-orange-600">{m(ssp.total_profit)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* DSP Section */}
      <Card data-testid="dsp-billing-section">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ArrowUpRight className="h-5 w-5 text-emerald-500" />
            DSP — Demand Side
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* DSP Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-900" data-testid="dsp-impressions-card">
              <p className="text-xs text-muted-foreground uppercase">Impressions</p>
              <p className="text-2xl font-bold text-violet-600 mt-1">{f(dsp.total_impressions)}</p>
            </div>
            <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900" data-testid="dsp-revenue-card">
              <p className="text-xs text-muted-foreground uppercase">Revenue (DSP Pays Us)</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{m(dsp.total_revenue)}</p>
            </div>
            <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900" data-testid="dsp-profit-card">
              <p className="text-xs text-muted-foreground uppercase">DSP Profit (Our Margin)</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">{m(dsp.total_profit)}</p>
            </div>
          </div>

          {/* DSP Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Company / Endpoint</TableHead>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">Impressions</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dsp.companies.map(c => (
                <CompanyGroup
                  key={c.company_id}
                  company={c}
                  expanded={expandedDSP[c.company_id]}
                  onToggle={() => setExpandedDSP(p => ({...p, [c.company_id]: !p[c.company_id]}))}
                  moneyField="revenue"
                  profitField="profit"
                  moneyColor="text-emerald-600"
                  profitColor="text-purple-600"
                />
              ))}
              {dsp.companies.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No DSP data</TableCell></TableRow>
              )}
              {dsp.companies.length > 0 && (
                <TableRow className="bg-muted/30 font-semibold border-t-2">
                  <TableCell /><TableCell>Total</TableCell><TableCell />
                  <TableCell className="text-right font-mono text-violet-600">{dsp.total_impressions.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-emerald-600">{m(dsp.total_revenue)}</TableCell>
                  <TableCell className="text-right font-mono text-purple-600">{m(dsp.total_profit)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Platform Total */}
      <Card className="border-l-4 border-l-emerald-500" data-testid="platform-profit-card">
        <CardContent className="pt-4 pb-3 px-6 flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Total Platform Profit</p>
            <p className="text-3xl font-bold text-emerald-600 mt-1">{m(data.total_platform_profit)}</p>
          </div>
          <div className="text-right text-sm text-muted-foreground space-y-1">
            <p>SSP Profit: <span className="font-mono text-orange-600">{m(ssp.total_profit)}</span></p>
            <p>DSP Profit: <span className="font-mono text-purple-600">{m(dsp.total_profit)}</span></p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CompanyGroup({ company, expanded, onToggle, moneyField, profitField, moneyColor, profitColor }) {
  const m = (n) => (n == null) ? "$0.000" : `$${Number(n).toFixed(3)}`;
  return (
    <>
      <TableRow className="bg-muted/20 cursor-pointer hover:bg-muted/40" onClick={onToggle} data-testid={`company-row-${company.company_id.slice(0, 8)}`}>
        <TableCell className="w-8 pr-0">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </TableCell>
        <TableCell className="font-semibold flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          {company.company_name}
          <span className="text-xs text-muted-foreground font-normal">({company.endpoints.length} ep)</span>
        </TableCell>
        <TableCell />
        <TableCell className="text-right font-mono font-medium text-blue-600">{company.impressions.toLocaleString()}</TableCell>
        <TableCell className={`text-right font-mono font-semibold ${moneyColor}`}>{m(company[moneyField])}</TableCell>
        <TableCell className={`text-right font-mono font-semibold ${profitColor}`}>{m(company[profitField])}</TableCell>
      </TableRow>
      {expanded && company.endpoints.map(ep => (
        <TableRow key={ep.id} className="text-sm" data-testid={`endpoint-row-${ep.id.slice(0, 8)}`}>
          <TableCell />
          <TableCell className="pl-10 text-muted-foreground">{ep.name}</TableCell>
          <TableCell className="text-xs text-muted-foreground">{ep.region || "—"}</TableCell>
          <TableCell className="text-right font-mono text-blue-600">{ep.impressions.toLocaleString()}</TableCell>
          <TableCell className={`text-right font-mono ${moneyColor}`}>{m(ep[moneyField])}</TableCell>
          <TableCell className={`text-right font-mono ${profitColor}`}>{m(ep[profitField])}</TableCell>
        </TableRow>
      ))}
    </>
  );
}
